<template>
    <div class="line-chart">
        <h5 v-if="tableName">
            {{ tableName }}
        </h5>

        <span class="sub-header" v-if="subHeader">
			{{ subHeader }}
		</span>

        <span class="timestamp" v-if="timestamp">
            <p class="label">Timestamp: </p>
            {{ timestamp }}
        </span>

        <section class="chart-container">
            <canvas :id="uniqueId"/>
        </section>
    </div>
</template>

<script>
import Chart from 'chart.js'
export default {
	props: {
		tableName: {
			type: String,
			default: ''
		},

		timestamp: {
			type: String,
			default: ''
		},

		subHeader: {
			type: String,
			default: ''
		},

		dataType: {
			type: String,
			default: ''
		},

		labels: {
			type: Array,
			default: () => []
		},

		dataSet: {
			type: Array,
			default: () => []
		},

		color: {
			type: Array,
			default: () => []
		},

		emotionLevels: {
			type: Array,
			default: () => []
		},

		stressLevels: {
			type: Array,
			default: () => []
		},

		showLabel: {
			type: Boolean,
			default: true
		}
	},

	data () {
		return {
			uniqueId: '',
			chart: null
		}
	},

	watch: {
		dataSet (val) {
			this.chart.data.datasets[0] = {
				data: val,
				borderColor: '#434343',
				backgroundColor: '#7A7A7A',
				pointRadius: ({dataIndex}) => {
					if (this.dataSet.length === 0) return 0
					const value = this.dataSet[dataIndex].count
					const percentage = value / this.highestCount * 100

					if (Math.round(percentage) <= 20) return 4
					if (Math.round(percentage) > 20 && Math.round(percentage) <= 40) return 6
					if (Math.round(percentage) > 40 && Math.round(percentage) <= 60) return 8
					if (Math.round(percentage) > 60 && Math.round(percentage) <= 80) return 10
					if (Math.round(percentage) > 80 && Math.round(percentage) <= 100) return 12
				},
				pointHoverRadius: ({dataIndex}) => {
					if (this.dataSet.length === 0) return 0
					const value = this.dataSet[dataIndex].count
					const percentage = value / this.highestCount * 100

					if (Math.round(percentage) <= 20) return 4
					if (Math.round(percentage) > 20 && Math.round(percentage) <= 40) return 6
					if (Math.round(percentage) > 40 && Math.round(percentage) <= 60) return 8
					if (Math.round(percentage) > 60 && Math.round(percentage) <= 80) return 10
					if (Math.round(percentage) > 80 && Math.round(percentage) <= 100) return 12
				}
			}

			this.chart.update()
		}
	},

	created () {
		this.generateUniqueId()
	},

	mounted () {
		this.renderLineChart()
	},

	computed: {
		highestCount () {
			const arr = this.dataSet.map(x => x.count)
			return Math.max(...arr)
		}
	},

	methods: {
		/* Created Methods */
		generateUniqueId () {
			this.uniqueId = Math.random().toString(36).substr(2, 9)
		},

		/* Mounted Methods */
		renderLineChart () {
			const lineChartCanvas = document.getElementById(this.uniqueId)

			// const data = this.setData()
			// const DATA_COUNT = 7
			// const NUMBER_CFG = {count: DATA_COUNT, min: -100, max: 100}
			const data = {
				datasets: [{
					data: this.dataSet,
					borderColor: '#434343',
					backgroundColor: '#7A7A7A',
					pointRadius: ({dataIndex}) => {
						if (this.dataSet.length === 0) return 0
						const value = this.dataSet[dataIndex].count
						const percentage = value / this.highestCount * 100

						if (Math.round(percentage) <= 20) return 4
						if (Math.round(percentage) > 20 && Math.round(percentage) <= 40) return 6
						if (Math.round(percentage) > 40 && Math.round(percentage) <= 60) return 8
						if (Math.round(percentage) > 60 && Math.round(percentage) <= 80) return 10
						if (Math.round(percentage) > 80 && Math.round(percentage) <= 100) return 12
					},
					pointHoverRadius: ({dataIndex}) => {
						if (this.dataSet.length === 0) return 0
						const value = this.dataSet[dataIndex].count
						const percentage = value / this.highestCount * 100

						if (Math.round(percentage) <= 20) return 4
						if (Math.round(percentage) > 20 && Math.round(percentage) <= 40) return 6
						if (Math.round(percentage) > 40 && Math.round(percentage) <= 60) return 8
						if (Math.round(percentage) > 60 && Math.round(percentage) <= 80) return 10
						if (Math.round(percentage) > 80 && Math.round(percentage) <= 100) return 12
					}
				}]
			}

			const options = this.setOptions()

			const quadrants = {
				id: 'quadrants',
				beforeDraw (chart, args, options) {
					const {
						ctx,
						chartArea: { left, top },
						scales
					} = chart

					ctx.save()

					ctx.fillStyle = options['11']
					ctx.fillRect(left, top, scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['12']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(-4), top, scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['13']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(-3), top, scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['14']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(-2), top, scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['15']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(-1), top, scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['16']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(0), top, scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['17']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(1), top, scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['18']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(2), top, scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['19']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(3), top, scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['10']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(4), top, scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)

					ctx.fillStyle = options['21']
					ctx.fillRect(left, scales['y-axis-1'].getPixelForValue(2), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['22']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(-4), scales['y-axis-1'].getPixelForValue(2), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['23']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(-3), scales['y-axis-1'].getPixelForValue(2), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['24']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(-2), scales['y-axis-1'].getPixelForValue(2), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['25']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(-1), scales['y-axis-1'].getPixelForValue(2), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['26']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(0), scales['y-axis-1'].getPixelForValue(2), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['27']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(1), scales['y-axis-1'].getPixelForValue(2), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['28']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(2), scales['y-axis-1'].getPixelForValue(2), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['29']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(3), scales['y-axis-1'].getPixelForValue(2), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['20']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(4), scales['y-axis-1'].getPixelForValue(2), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)

					ctx.fillStyle = options['31']
					ctx.fillRect(left, scales['y-axis-1'].getPixelForValue(1), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['32']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(-4), scales['y-axis-1'].getPixelForValue(1), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['33']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(-3), scales['y-axis-1'].getPixelForValue(1), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['34']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(-2), scales['y-axis-1'].getPixelForValue(1), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['35']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(-1), scales['y-axis-1'].getPixelForValue(1), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['36']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(0), scales['y-axis-1'].getPixelForValue(1), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['37']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(1), scales['y-axis-1'].getPixelForValue(1), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['38']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(2), scales['y-axis-1'].getPixelForValue(1), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['39']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(3), scales['y-axis-1'].getPixelForValue(1), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['30']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(4), scales['y-axis-1'].getPixelForValue(1), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)

					ctx.fillStyle = options['41']
					ctx.fillRect(left, scales['y-axis-1'].getPixelForValue(0), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['42']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(-4), scales['y-axis-1'].getPixelForValue(0), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['43']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(-3), scales['y-axis-1'].getPixelForValue(0), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['44']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(-2), scales['y-axis-1'].getPixelForValue(0), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['45']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(-1), scales['y-axis-1'].getPixelForValue(0), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['46']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(0), scales['y-axis-1'].getPixelForValue(0), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['47']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(1), scales['y-axis-1'].getPixelForValue(0), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['48']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(2), scales['y-axis-1'].getPixelForValue(0), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['49']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(3), scales['y-axis-1'].getPixelForValue(0), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['40']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(4), scales['y-axis-1'].getPixelForValue(0), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)

					ctx.fillStyle = options['51']
					ctx.fillRect(left, scales['y-axis-1'].getPixelForValue(-1), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['52']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(-4), scales['y-axis-1'].getPixelForValue(-1), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['53']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(-3), scales['y-axis-1'].getPixelForValue(-1), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['54']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(-2), scales['y-axis-1'].getPixelForValue(-1), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['55']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(-1), scales['y-axis-1'].getPixelForValue(-1), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['56']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(0), scales['y-axis-1'].getPixelForValue(-1), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['57']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(1), scales['y-axis-1'].getPixelForValue(-1), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['58']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(2), scales['y-axis-1'].getPixelForValue(-1), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['59']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(3), scales['y-axis-1'].getPixelForValue(-1), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)
					ctx.fillStyle = options['50']
					ctx.fillRect(scales['x-axis-1'].getPixelForValue(4), scales['y-axis-1'].getPixelForValue(-1), scales['x-axis-1'].getPixelForValue(-4) - left, scales['y-axis-1'].getPixelForValue(2) - top)

					ctx.restore()
				}
			}

			Chart.defaults.global.defaultFontColor = '#999'

			// eslint-disable-next-line
			this.chart = new Chart(
				lineChartCanvas,
				{
					type: 'scatter',
					data: data,
					options: options,
					plugins: [quadrants]
				}
			)
		},

		/* Set Data Methods */
		setData () {
			const data = {
				labels: this.setDataLabels(),

				datasets: this.setDataSets()
			}

			return data
		},

		setDataLabels () {
			const labels = this.labels.length === 1
				? ['', ...this.labels, '']
				: this.labels

			return labels
		},

		setDataSets () {
			const dataSet = [
				{
					data: this.setDataSetData(),
					borderColor: '#61DBFF',
					backgroundColor: 'rgba(177, 237, 255, 0.4)',
					pointBackgroundColor: 'rgba(62, 207, 250, 0.5)',
					fill: true
				}
			]

			if (this.dataType === 'stress') {
				dataSet[0].borderColor = '#fdea84'
				dataSet[0].backgroundColor = 'rgba(254, 246, 202, 0.4)'
				dataSet[0].pointBackgroundColor = 'rgba(252, 229, 104, 0.5)'
			}

			return dataSet
		},

		/* Set Option Methods */
		setOptions () {
			const option = {
				legend: {
					display: false
				},

				animation: {
					duration: 0
				},

				layout: {
					padding: {
						top: 12
					}
				},

				hover: {
					animationDuration: 0
				},

				responsiveAnimationDuration: 0,

				responsive: true,

				tooltips: {
					enabled: true,
					displayColors: false,
					bodyFontFamily: "'Quicksand', sans-serif",
					bodyFontSize: 12,
					callbacks: {
						label: function (tooltipItem, data) {
							let valence = ''
							let arousal = ''
							if (tooltipItem.xLabel === -4) valence = 'Severe Negativity'
							if (tooltipItem.xLabel === -3) valence = 'High Negativity'
							if (tooltipItem.xLabel === -2) valence = 'Moderate Negativity'
							if (tooltipItem.xLabel === -1) valence = 'Low Negativity'
							if (tooltipItem.xLabel === 0) valence = 'Neutral'
							if (tooltipItem.xLabel === 1) valence = 'Low Positivity'
							if (tooltipItem.xLabel === 2) valence = 'Moderate Positivity'
							if (tooltipItem.xLabel === 3) valence = 'High Positivity'
							if (tooltipItem.xLabel === 4) valence = 'Intense Positivity'

							if (tooltipItem.yLabel === 2) arousal = 'High'
							if (tooltipItem.yLabel === 1) arousal = 'Medium'
							if (tooltipItem.yLabel === 0) arousal = 'Neutral'
							if (tooltipItem.yLabel === -1) arousal = 'Low'
							return [
								`Frequency: ${data.datasets[0].data[tooltipItem.index].count}`,
								`Valence: ${valence}`,
								`Arousal: ${arousal}`
							]
						}
					}
				},

				// elements: {
				// 	point: {
				// 		radius: 5,
				// 		hoverRadius: 7,
				// 		borderColor: '#434343',
				// 	    backgroundColor: '#7A7A7A'
				// 	}
				// },

				plugins: {
					quadrants: {
						'11': '#821444',
						'12': '#831F42',
						'13': '#942546',
						'14': '#B37E53',
						'15': '#DCB83C',
						'16': '#DCB83C',
						'17': '#C0CE65',
						'18': '#9BCC78',
						'19': '#72BD96',
						'10': '#66B599',

						'21': '#9D1852',
						'22': '#AE2E4D',
						'23': '#BA3B50',
						'24': '#C6763A',
						'25': '#F3DF46 ',
						'26': '#F3DF46 ',
						'27': '#D3D653',
						'28': '#9ED081',
						'29': '#82C383',
						'20': '#69BD6A',

						'31': '#C43257',
						'32': '#BC623B',
						'33': '#CB6F37',
						'34': '#D2A638 ',
						'35': '#F6E66C ',
						'36': '#F5E56B ',
						'37': '#CADD6B',
						'38': '#AFDA91 ',
						'39': '#9FCF7C',
						'30': '#8DCB5F',

						'41': '#CD5F3D',
						'42': '#CD823D',
						'43': '#D09D39',
						'44': '#DCC250',
						'45': '#FAF2B5',
						'46': '#FAF1B5',
						'47': '#CFE393 ',
						'48': '#BDE09A',
						'49': '#A8D998',
						'40': '#95DD98',

						'51': '#CD7137',
						'52': '#D09D39',
						'53': '#D4CB80',
						'54': '#D4D7A1',
						'55': '#CBE4DA',
						'56': '#C0E0C5',
						'57': '#C9E5B2',
						'58': '#C1E3A7',
						'59': '#C1E3A7',
						'50': '#C5EEA6'
					}
				},

				scales: this.setScales()
			}

			return option
		},

		setDataSetData () {
			return this.dataSet.length === 1
				? [{}, ...this.dataSet, {}]
				: this.dataSet
		},

		setScales () {
			const scales = {
				xAxes: [{
					display: true,

					ticks: {
						min: -5,
						max: 5,
						stepSize: 1,
						fontFamily: "'Quicksand', sans-serif",
						fontSize: 12,
						fontColor: '#434343',
						fontStyle: 'bold'
					},

					gridLines: {
						display: true,
						color: 'transparent',
						zeroLineColor: '#797149',
						zeroLineWidth: 1
					},

					scaleLabel: {
						display: true,
						labelString: 'Valence',
						fontFamily: "'Quicksand', sans-serif",
						fontSize: 14,
						fontColor: '#434343',
						fontStyle: 'bold',
						lineHeight: 0
					}
				},
				{
					display: true,
					gridLines: {display: false, color: 'transparent'},
					ticks: {display: false},
					scaleLabel: {
						display: this.showLabel,
						labelString: 'how positive/negative an emotion is',
						fontFamily: "'Quicksand', sans-serif",
						fontSize: 14,
						fontColor: '#434343',
						fontStyle: 'italic',
						lineHeight: '0.4'
					}
				}],

				yAxes: [{
					display: true,

					ticks: {
						min: -2,
						max: 3,
						stepSize: 1,
						fontFamily: "'Quicksand', sans-serif",
						fontSize: 12,
						fontColor: '#434343',
						fontStyle: 'bold'
					},

					gridLines: {
						display: true,
						color: 'transparent',
						zeroLineColor: '#797149',
						zeroLineWidth: 1
					},

					scaleLabel: {
						display: this.showLabel,
						labelString: 'how intense an emotion is',
						fontFamily: "'Quicksand', sans-serif",
						fontSize: 14,
						fontColor: '#434343',
						fontStyle: 'italic',
						lineHeight: 0
					}
				},
				{
					display: true,
					gridLines: {display: false, color: 'transparent'},
					ticks: {display: false},
					scaleLabel: {
						display: true,
						labelString: 'Arousal',
						fontFamily: "'Quicksand', sans-serif",
						fontSize: 14,
						fontColor: '#434343',
						fontStyle: 'bold',
						lineHeight: '0.4'
					}
				}]
			}

			// scales.yAxes[0].ticks.max = this.emotionLevels.length - 1

			// if (this.dataType === 'stress') {
			// 	scales.yAxes[0].ticks.max = 3
			// }

			// if (this.dataType === 'bpm') {
			// 	scales.yAxes[0].ticks.max = 200
			// 	scales.yAxes[0].ticks.stepSize = 50
			// }

			return scales
		},

		setYaxisLabel (label, index, labels) {
			if (this.dataType === 'bpm') return label

			if (this.dataType === 'stress') {
				return this.stressLevels[label]
			}

			return this.emotionLevels[label]
		}
	}
}
</script>

<style lang="scss" scoped>
.line-chart {
    width: 90%;
    height: 100%;

    h5 {
        font-size: 12px;
        font-weight: 500;
        margin-bottom: 10px;
    }

    .sub-header {
		display: block;
		font-size: 12px;
		color: #5e5e5e;
		margin: 3px 0 12px 0;
	}

    .timestamp {
        display: block;
        text-align: center;
        margin-bottom: 10px;
        font-weight: 400;

        .label {
            font-weight: 700;
            display: inline-block;
        }
    }

    .chart-legend {
        position: absolute;
        top: 70px;
        left: 0;

        .legend-item {
            display: flex;
            align-items: center;
            font-size: 13px;

            &:first-child {
                margin-bottom: 5px;
            }

            .legend-icon {
                display: inline-block;
                width: 23px;
                height: 10px;
                border-radius: 10px;
                margin-right: 10px;
                flex-shrink: 0;

                &.overall {
                    background: #d7d7fc;
                }

                &.user {
                    background: #3b86ff;
                }
            }
        }
    }

    .chart-container {
        width: 100%;
        margin: 0 auto;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/app-components/AppChartQuadrant.vue